import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Lock, ArrowRight, Zap, Crown, FlaskConical, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['All', 'Free', 'Premium', 'Memory', 'Thinking', 'Career', 'Communication', 'Behavior'];

function ReviewerBadge({ program }) {
  const name = program.reviewer_name;
  const initials = program.reviewer_initials;
  if (name) {
    return (
      <div
        className="flex items-center gap-2.5"
        title="Reviewed by a learning design or behavioral expert to ensure structured and effective methodology."
        data-testid={`reviewer-${program.program_id}`}
      >
        <div className="w-7 h-7 bg-[#09090B] text-white flex items-center justify-center text-[10px] font-bold tracking-wider flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {initials || name.split(' ').map(n => n[0]).join('').slice(0, 2)}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-zinc-400 leading-none mb-0.5">Reviewed by</p>
          <p className="text-xs font-medium text-zinc-700 truncate">{name}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid={`badge-${program.program_id}`} title="Designed using established research principles.">
      <FlaskConical className="w-3.5 h-3.5 text-[#09090B] flex-shrink-0" />
      <span className="text-xs font-medium text-zinc-500">Research-Based Program</span>
    </div>
  );
}

export default function Programs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const isPro = user?.subscription?.plan === 'pro' || user?.subscription?.plan === 'enterprise';

  useEffect(() => {
    axios.get(`${API}/programs`).then(res => setPrograms(res.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = activeCategory === 'All'
    ? programs
    : activeCategory === 'Free'
      ? programs.filter(p => p.is_free)
      : activeCategory === 'Premium'
        ? programs.filter(p => !p.is_free)
        : programs.filter(p => p.category === activeCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="programs-loading">
        <div className="mono-data text-lg animate-pulse-red">Loading Programs...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="programs-page">
      <div className="border-b border-[#09090B] py-12 md:py-16">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-4">Training Library</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="programs-heading">Programs</h1>
          <p className="text-lg text-zinc-600 mt-4 max-w-2xl">26 structured programs across 6 cognitive domains. Free programs and the BDNF Protocol bonus available immediately.</p>
        </div>
      </div>

      {/* Pro upsell banner — only logged-in non-Pro users */}
      {user && !isPro && (
        <div className="border-b border-[#09090B] bg-[#09090B] text-white" data-testid="programs-pro-banner">
          <div className="container-swiss py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Crown className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">
                <span className="font-bold">Unlock all 21 premium programs</span>
                <span className="text-zinc-400"> · ₹299 · 30 days</span>
              </p>
            </div>
            <Link
              to="/pricing"
              className="bg-white text-[#09090B] px-5 py-2 text-xs font-bold tracking-widest uppercase hover:bg-zinc-200 transition-colors flex items-center gap-2 flex-shrink-0"
              data-testid="programs-upgrade-cta"
            >
              Upgrade to Pro <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      )}

      <div className="border-b border-[#09090B]">
        <div className="container-swiss flex flex-wrap gap-0">
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)}
              className={`px-6 py-3 text-sm font-medium tracking-wide uppercase border-r border-[#09090B] transition-colors ${activeCategory === cat ? 'bg-[#09090B] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
              data-testid={`filter-${cat.toLowerCase()}`}>{cat}</button>
          ))}
        </div>
      </div>

      <div className="container-swiss py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-[#09090B]">
          {filtered.map(program => (
            <Link to={`/programs/${program.program_id}`} key={program.program_id}
              className="border-r border-b border-[#09090B] group flex flex-col" data-testid={`program-card-${program.program_id}`}>

              {/* Image */}
              <div className="aspect-[16/10] border-b border-[#09090B] overflow-hidden bg-zinc-100 relative">
                {program.image_url ? (
                  <img src={program.image_url} alt={program.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ filter: 'grayscale(100%) contrast(1.05)' }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-zinc-50">
                    <span className="mono-data text-zinc-300 text-xs">{program.category}</span>
                  </div>
                )}
                {!program.is_free && (
                  <div className="absolute top-3 right-3 bg-[#09090B] text-white px-3 py-1 flex items-center gap-1">
                    <Lock className="w-3 h-3" /><span className="mono-data text-xs">Premium</span>
                  </div>
                )}
                {program.is_free && !program.is_bonus && (
                  <div className="absolute top-3 right-3 bg-[#09090B] text-white px-3 py-1">
                    <span className="mono-data text-xs">Free</span>
                  </div>
                )}
                {program.is_bonus && (
                  <div className="absolute top-3 right-3 bg-[#09090B] text-white px-3 py-1 flex items-center gap-1">
                    <span className="mono-data text-xs font-bold tracking-widest">★ BONUS</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="mono-data text-xs text-[#09090B]">{program.category}</span>
                  <span className="mono-data text-xs text-zinc-400">{program.session_count} sessions</span>
                </div>
                <h3 className="text-lg font-semibold tracking-tight mb-2 group-hover:text-[#09090B] transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {program.title}
                </h3>
                <p className="text-sm text-zinc-600 leading-relaxed line-clamp-2 mb-4">{program.description}</p>

                {/* === PRICING === */}
                {program.is_free ? (
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-bold tracking-wide uppercase text-[#09090B]">Start Free</span>
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                ) : (
                  <div className="mb-4" data-testid={`pricing-${program.program_id}`}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>₹{program.price}</span>
                        <span className="mono-data text-xs text-zinc-400">individual</span>
                      </div>
                      {isPro && (
                        <span className="flex items-center gap-1 text-xs font-bold text-[#09090B]" data-testid="pro-included-badge">
                          <Crown className="w-3 h-3" /> Included
                        </span>
                      )}
                    </div>
                    {!isPro && (
                      <>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Zap className="w-3 h-3 text-[#09090B] flex-shrink-0" />
                          <span className="text-xs font-medium text-[#09090B]" data-testid="pro-value-line">Included in Pro plan</span>
                        </div>
                        <p className="text-[11px] text-zinc-400 mb-2">Access 25+ programs with Pro subscription</p>
                      </>
                    )}
                    <div className="flex items-center gap-2">
                      {isPro ? (
                        <span className="text-sm font-bold tracking-wide uppercase text-[#09090B] flex items-center gap-1">
                          Start Program <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </span>
                      ) : (
                        <>
                          <span className="text-xs font-bold tracking-wide uppercase">Buy Now</span>
                          <span className="text-zinc-300">|</span>
                          <span className="text-xs font-medium text-[#09090B] tracking-wide uppercase hover:underline cursor-pointer"
                            onClick={e => { e.preventDefault(); e.stopPropagation(); navigate('/pricing'); }}
                            data-testid="get-pro-link">Get Pro</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* === REVIEWER BADGE (bottom of card) === */}
                <div className="mt-auto pt-4 border-t border-zinc-200">
                  <ReviewerBadge program={program} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
